import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Check,
    Key,
    Link2,
    Server,
    Trash2,
    Zap,
    Star,
    Layers,
    Plus,
    Lock
} from 'lucide-react';

import { PageLayout } from '@/components/page-layout';
import { useLayout } from '@/contexts/layout-context';
import { http } from '@/libs/http';
import type { EmailServiceConfig } from './email-service-page';
import { ConfirmDeleteModal } from '@/features/mail/confirm-delete-modal';

const HTTP_METHODS = ['POST', 'PUT', 'PATCH', 'GET'];
const PROVIDER_TYPES = [
    { id: 'logic_apps', label: 'Azure Logic Apps (HTTP Webhook)' }
];

export const ManageEmailServicePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    const [config, setConfig] = useState<EmailServiceConfig | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [name, setName] = useState('');
    const [serviceType, setServiceType] = useState('logic_apps');
    const [httpMethod, setHttpMethod] = useState('POST');
    const [url, setUrl] = useState('');
    const [subjectField, setSubjectField] = useState('subject');
    const [bodyField, setBodyField] = useState('body');
    const [toField, setToField] = useState('to');
    const [ccField, setCcField] = useState('cc');
    const [bccField, setBccField] = useState('bcc');
    const [attachmentsField, setAttachmentsField] = useState('attachments');
    const [customMappings, setCustomMappings] = useState<{ key: string; value: string }[]>([]);
    const [priority, setPriority] = useState('normal');
    const [isActive, setIsActive] = useState(true);
    const [isDefault, setIsDefault] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
        if (!id) return;
        setIsLoading(true);
        http.get<EmailServiceConfig>(`/mgmt/v1/configurations/email-service/${id}`)
            .then(data => {
                setConfig(data);
                setName(data.name);
                setServiceType(data.service_type || 'logic_apps');
                setHttpMethod(data.http_method || 'POST');
                setUrl(data.provider?.masked_url || '');

                const map = data.field_mapping || {};
                setSubjectField(map.subject || 'subject');
                setBodyField(map.body || 'body');
                setToField(map.to || 'to');
                setCcField(map.cc || 'cc');
                setBccField(map.bcc || 'bcc');
                setAttachmentsField(map.attachments || 'attachments');

                const stdKeys = ['subject', 'body', 'to', 'cc', 'bcc', 'attachments'];
                const custom: { key: string; value: string }[] = [];
                Object.entries(map).forEach(([k, v]) => {
                    if (!stdKeys.includes(k) && typeof v === 'string') {
                        custom.push({ key: k, value: v });
                    }
                });
                setCustomMappings(custom);

                setPriority(data.priority || 'normal');
                setIsActive(data.is_active);
                setIsDefault(data.is_default);

                setBreadcrumbs([
                    { name: 'Configurations', to: '/m/configurations' },
                    { name: 'Email Service', to: '/m/configurations/email' },
                    { name: data.name }
                ]);
            })
            .catch(() => {
                toast.error('Failed to load email service configuration');
                navigate('/m/configurations/email');
            })
            .finally(() => setIsLoading(false));
    }, [id, navigate, setBreadcrumbs]);

    const handleAddCustomMapping = () => {
        setCustomMappings(prev => [...prev, { key: '', value: '' }]);
    };

    const handleRemoveCustomMapping = (index: number) => {
        setCustomMappings(prev => prev.filter((_, i) => i !== index));
    };

    const handleCustomMappingChange = (index: number, field: 'key' | 'value', val: string) => {
        setCustomMappings(prev => {
            const next = [...prev];
            next[index][field] = val;
            return next;
        });
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id || !config) return;
        if (!name.trim()) {
            toast.error('Connection name is required');
            return;
        }

        // Only send URL if user changed it from the masked URL
        const payloadUrl = (url && url.trim() !== config.provider?.masked_url) ? url.trim() : undefined;

        const fieldMappingObj: Record<string, string> = {
            subject: subjectField.trim() || 'subject',
            body: bodyField.trim() || 'body',
            to: toField.trim() || 'to',
            cc: ccField.trim() || 'cc',
            bcc: bccField.trim() || 'bcc',
            attachments: attachmentsField.trim() || 'attachments',
        };

        customMappings.forEach(item => {
            const k = item.key.trim();
            const v = item.value.trim();
            if (k && v && !['subject', 'body', 'to', 'cc', 'bcc', 'attachments'].includes(k)) {
                fieldMappingObj[k] = v;
            }
        });

        setIsSaving(true);
        try {
            await http.put(`/mgmt/v1/configurations/email-service/${id}`, {
                name: name.trim(),
                http_method: httpMethod,
                url: payloadUrl,
                field_mapping: fieldMappingObj,
                priority,
                is_active: isActive
            });
            toast.success('Connection updated successfully');
            navigate('/m/configurations/email');
        } catch (err: any) {
            toast.error(err.message || 'Failed to update connection');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!id) return;
        try {
            await http.delete(`/mgmt/v1/configurations/email-service/${id}`);
            toast.success('Connection deleted successfully');
            navigate('/m/configurations/email');
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete connection');
        }
    };

    const handleSetDefault = async () => {
        if (!id) return;
        try {
            await http.post(`/mgmt/v1/configurations/email-service/${id}/set-default`, {});
            setIsDefault(true);
            toast.success('Set as default email service provider');
        } catch (err: any) {
            toast.error('Failed to set default provider');
        }
    };

    const handleCopyKvRef = () => {
        if (!config) return;
        const ref = config.provider?.url_ref || config.kv_secret_name;
        navigator.clipboard.writeText(ref);
        toast.success('Key Vault secret reference copied to clipboard');
    };

    if (isLoading || !config) {
        return (
            <PageLayout title="Manage Connection" description="Loading connection details...">
                <div className="flex items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-(--primary)"></div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title={config.name}
            description="Manage email service connection details, endpoint URL, and field mappings."
            action={
                <div className="flex items-center gap-3">
                    <Link
                        to="/m/configurations/email"
                        className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to Configurations
                    </Link>
                    {!isDefault && (
                        <button
                            type="button"
                            onClick={handleSetDefault}
                            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-lg hover:bg-amber-100 transition-colors"
                        >
                            <Star className="w-4 h-4 text-amber-500" /> Set as Default
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowDeleteModal(true)}
                        className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg transition-colors"
                    >
                        <Trash2 className="w-4 h-4" /> Delete
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-(--primary) hover:bg-(--primary-hover) disabled:opacity-50 rounded-lg transition-colors shadow-sm"
                    >
                        <Check className="w-4 h-4" /> {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            }
        >
            <form onSubmit={handleSave} className="max-w-4xl space-y-8 animate-in fade-in duration-300">

                {/* SECTION 1: TOP SECTION (CONNECTION NAME & ENABLED TOGGLE) */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs p-6 space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-2.5">
                            <Zap className="w-5 h-5 text-(--primary)" />
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Connection Details</h3>
                        </div>
                        <div className="flex items-center gap-3">
                            {isDefault && (
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-(--primary)/10 text-(--primary)">
                                    <Star className="w-3.5 h-3.5" /> Default Service
                                </span>
                            )}
                            <label className="flex items-center gap-2.5 p-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={e => setIsActive(e.target.checked)}
                                    className="w-4 h-4 text-(--primary) rounded focus:ring-(--primary)"
                                />
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                    {isActive ? 'Enabled' : 'Disabled'}
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                            Connection Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            className="w-full px-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary) text-slate-900 dark:text-white"
                            required
                        />
                    </div>
                </div>

                {/* SECTION 2: PROVIDER SECTION (DROPDOWN + URL & HTTP METHOD FOR LOGIC APPS) */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs p-6 space-y-6">
                    <div className="flex items-center gap-2.5 pb-4 border-b border-slate-100 dark:border-slate-800">
                        <Server className="w-5 h-5 text-(--primary)" />
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">Provider Configuration</h3>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                            Provider Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={serviceType}
                            onChange={e => setServiceType(e.target.value)}
                            className="w-full px-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary) text-slate-900 dark:text-white font-medium"
                        >
                            {PROVIDER_TYPES.map(p => (
                                <option key={p.id} value={p.id}>{p.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Logic App Fields: Editable URL (Masked) & HTTP Method */}
                    {serviceType === 'logic_apps' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                            <div className="md:col-span-2 space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="block text-sm font-bold text-slate-900 dark:text-white">
                                        Logic App URL
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleCopyKvRef}
                                        title="Copy Key Vault Secret Reference"
                                        className="p-1.5 text-amber-600 hover:text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-md transition-colors flex items-center gap-1.5 text-xs font-semibold"
                                    >
                                        <Key className="w-3.5 h-3.5 text-amber-500" />
                                    </button>
                                </div>
                                <div className="relative">
                                    <Link2 className="w-4 h-4 absolute left-3.5 top-3 text-slate-400" />
                                    <input
                                        type="text"
                                        value={url}
                                        onChange={e => setUrl(e.target.value)}
                                        placeholder="https://prod-12.eastus.logic.azure.com:443/workflows/..."
                                        className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary) font-mono text-slate-900 dark:text-white"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">HTTP Method</label>
                                <select
                                    value={httpMethod}
                                    onChange={e => setHttpMethod(e.target.value)}
                                    className="w-full px-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary) text-slate-900 dark:text-white font-mono font-semibold"
                                >
                                    {HTTP_METHODS.map(m => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* SECTION 3: MAPPING SECTION (DYNAMIC FIELD MAPPINGS) */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs p-6 space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-2.5">
                            <Layers className="w-5 h-5 text-(--primary)" />
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Field Mapping</h3>
                                <p className="text-xs text-slate-500">Map template email fields to JSON request payload keys</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleAddCustomMapping}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-(--primary) bg-(--primary)/10 hover:bg-(--primary)/20 rounded-lg transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add Field Mapping
                        </button>
                    </div>

                    <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                        {/* Table Header */}
                        <div className="grid grid-cols-12 px-5 py-3 bg-slate-100 dark:bg-slate-800/80 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">
                            <div className="col-span-5">Field Key</div>
                            <div className="col-span-6">Mapped Payload Property</div>
                            <div className="col-span-1 text-right">Action</div>
                        </div>

                        {/* Standard Default Fields (Non-deletable) */}
                        <div className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-950 text-sm">
                            <div className="grid grid-cols-12 px-5 py-3.5 items-center gap-4">
                                <div className="col-span-5 flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                                    <Lock className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Subject</span>
                                </div>
                                <div className="col-span-6">
                                    <input
                                        type="text"
                                        value={subjectField}
                                        onChange={e => setSubjectField(e.target.value)}
                                        placeholder="subject"
                                        className="w-full px-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg font-mono text-slate-900 dark:text-white"
                                        required
                                    />
                                </div>
                                <div className="col-span-1 text-right text-xs text-slate-400 italic">Required</div>
                            </div>

                            <div className="grid grid-cols-12 px-5 py-3.5 items-center gap-4">
                                <div className="col-span-5 flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                                    <Lock className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Body</span>
                                </div>
                                <div className="col-span-6">
                                    <input
                                        type="text"
                                        value={bodyField}
                                        onChange={e => setBodyField(e.target.value)}
                                        placeholder="body"
                                        className="w-full px-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg font-mono text-slate-900 dark:text-white"
                                        required
                                    />
                                </div>
                                <div className="col-span-1 text-right text-xs text-slate-400 italic">Required</div>
                            </div>

                            <div className="grid grid-cols-12 px-5 py-3.5 items-center gap-4">
                                <div className="col-span-5 flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                                    <Lock className="w-3.5 h-3.5 text-slate-400" />
                                    <span>To (Recipient)</span>
                                </div>
                                <div className="col-span-6">
                                    <input
                                        type="text"
                                        value={toField}
                                        onChange={e => setToField(e.target.value)}
                                        placeholder="to"
                                        className="w-full px-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg font-mono text-slate-900 dark:text-white"
                                        required
                                    />
                                </div>
                                <div className="col-span-1 text-right text-xs text-slate-400 italic">Required</div>
                            </div>

                            <div className="grid grid-cols-12 px-5 py-3.5 items-center gap-4">
                                <div className="col-span-5 flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                                    <Lock className="w-3.5 h-3.5 text-slate-400" />
                                    <span>CC</span>
                                </div>
                                <div className="col-span-6">
                                    <input
                                        type="text"
                                        value={ccField}
                                        onChange={e => setCcField(e.target.value)}
                                        placeholder="cc"
                                        className="w-full px-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg font-mono text-slate-900 dark:text-white"
                                    />
                                </div>
                                <div className="col-span-1 text-right text-xs text-slate-400 italic">Standard</div>
                            </div>

                            <div className="grid grid-cols-12 px-5 py-3.5 items-center gap-4">
                                <div className="col-span-5 flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                                    <Lock className="w-3.5 h-3.5 text-slate-400" />
                                    <span>BCC</span>
                                </div>
                                <div className="col-span-6">
                                    <input
                                        type="text"
                                        value={bccField}
                                        onChange={e => setBccField(e.target.value)}
                                        placeholder="bcc"
                                        className="w-full px-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg font-mono text-slate-900 dark:text-white"
                                    />
                                </div>
                                <div className="col-span-1 text-right text-xs text-slate-400 italic">Standard</div>
                            </div>

                            <div className="grid grid-cols-12 px-5 py-3.5 items-center gap-4">
                                <div className="col-span-5 flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                                    <Lock className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Attachments</span>
                                </div>
                                <div className="col-span-6">
                                    <input
                                        type="text"
                                        value={attachmentsField}
                                        onChange={e => setAttachmentsField(e.target.value)}
                                        placeholder="attachments"
                                        className="w-full px-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg font-mono text-slate-900 dark:text-white"
                                    />
                                </div>
                                <div className="col-span-1 text-right text-xs text-slate-400 italic">Standard</div>
                            </div>

                            {/* Dynamic Custom Key Mappings */}
                            {customMappings.map((m, idx) => (
                                <div key={idx} className="grid grid-cols-12 px-5 py-3.5 items-center gap-4 animate-in fade-in duration-150">
                                    <div className="col-span-5">
                                        <input
                                            type="text"
                                            value={m.key}
                                            onChange={e => handleCustomMappingChange(idx, 'key', e.target.value)}
                                            placeholder="e.g. reply_to"
                                            className="w-full px-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg font-mono text-slate-900 dark:text-white font-semibold"
                                            required
                                        />
                                    </div>
                                    <div className="col-span-6">
                                        <input
                                            type="text"
                                            value={m.value}
                                            onChange={e => handleCustomMappingChange(idx, 'value', e.target.value)}
                                            placeholder="e.g. replyToAddress"
                                            className="w-full px-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg font-mono text-slate-900 dark:text-white"
                                            required
                                        />
                                    </div>
                                    <div className="col-span-1 text-right">
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveCustomMapping(idx)}
                                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 rounded transition-colors"
                                            title="Delete mapping"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Form Footer Action Bar */}
                <div className="flex items-center justify-end gap-3 pt-2">
                    <Link
                        to="/m/configurations/email"
                        className="px-5 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        disabled={isSaving}
                        className="px-6 py-2.5 text-sm font-semibold text-white bg-(--primary) hover:bg-(--primary-hover) disabled:opacity-50 rounded-lg transition-colors shadow-sm flex items-center gap-2"
                    >
                        <Check className="w-4 h-4" />
                        {isSaving ? 'Saving Changes...' : 'Save Changes'}
                    </button>
                </div>
            </form>

            <ConfirmDeleteModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Connection"
                itemName={config.name}
                description="Are you sure you want to delete this email service connection? Key Vault secret references will also be removed."
            />
        </PageLayout>
    );
};
